/**
 * Machine page › Reliability — projections for the four sub-views: SPC,
 * TARP, FMEA and control plan.
 *
 * Pure functions over the reliability context. The SPC chart comes from
 * `spcEngine` — the seam Miles's OptiFlow SPC module plugs into — so nothing
 * here depends on which engine computed the limits.
 *
 * Transformation layer (layer 3 of the 4-layer architecture): no React, no
 * mutation, no clock.
 *
 * @see src/versions/v1/modules/fleet/MachineReliabilityTab.tsx
 * @see docs/logic/fleet/machine-reliability.md
 */

import { BY_ID, HEALTH } from '../../data/core';
import type {
  ControlPlanRow,
  SpcResult,
  Tarp,
  TarpBasis,
  TarpLevelName,
  TarpStatus,
} from '../../schema/operations';
import {
  dailySeries,
  fmeaRows,
  liveSeries,
  MIN_POINTS,
  spcEngine,
  tarpStatuses,
  tarpsFor,
  type FmeaResult,
} from '../../shared/reliability';
import type { ReliabilityCtx } from '../../shared/reliability/useReliability';
import { CONTROL_PLANS } from '../../shared/seed/control-plans';
import { metricInfoFor } from '../../shared/seed/metric-info';
import type { LineHLine, LineMarker } from '../../shared/viz/LineChart';

// ---------------------------------------------------------------------------
// Sub-views
// ---------------------------------------------------------------------------

export const RELIABILITY_VIEWS = [
  { id: 'spc', label: 'SPC' },
  { id: 'tarp', label: 'TARP' },
  { id: 'fmea', label: 'FMEA' },
  { id: 'control', label: 'Control plan' },
] as const;

export type ReliabilityViewId = (typeof RELIABILITY_VIEWS)[number]['id'];

/** `?view=` → a known sub-view, defaulting to SPC. */
export function parseReliabilityView(raw: string | null): ReliabilityViewId {
  return RELIABILITY_VIEWS.some((v) => v.id === raw) ? (raw as ReliabilityViewId) : 'spc';
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `2026-09-08T07:57:00` → `08 Sep 07:57`; `2026-08-29` → `29 Aug`. */
export function stampLabel(x: string): string {
  const [date, time] = x.split('T');
  const [, m, d] = date.split('-');
  const day = `${d} ${MONTHS[Number(m) - 1] ?? m}`;
  return time ? `${day} ${time.slice(0, 5)}` : day;
}

const fix = (n: number, dp = 1): string => n.toFixed(dp);

/** `08 Sep` or `08–09 Sep` for a span of stamps. */
function spanDays(first: string, last: string): string {
  const a = stampLabel(first.split('T')[0]);
  const b = stampLabel(last.split('T')[0]);
  return a === b ? a : `${a.split(' ')[0]}–${b}`;
}

/** Live replay fields the daily-metric table does not name. */
const LIVE_METRIC_NAME: Readonly<Record<string, string>> = {
  brake_temp_rr_c: 'Rear brake temperature',
  intake_air_c: 'Intake air temperature',
};

// ---------------------------------------------------------------------------
// SPC
// ---------------------------------------------------------------------------

/** One run-rule signal, for the table under the chart. */
export interface SignalRow {
  key: string;
  index: number;
  at: string;
  value: string;
  rules: string;
  description: string;
}

export type SpcStateTone = 'crit' | 'good' | 'muted';

/** The I-MR chart and its stats, ready for `LineChart`. */
export interface SpcView {
  basis: 'live' | 'daily';
  title: string;
  subtitle: string;
  unit: string;
  labels: string[];
  values: number[];
  mr: (number | null)[];
  hlines: LineHLine[];
  mrHlines: LineHLine[];
  markers: LineMarker[];
  /** Y-axis floor/ceiling so the limits and the data both fit. */
  domain: { min: number; max: number };
  state: SpcResult['state'];
  stateLabel: string;
  stateTone: SpcStateTone;
  stats: readonly { label: string; value: string; hint?: string }[];
  signals: SignalRow[];
  /** Why the chart has no limits, when `state === 'insufficient'`. */
  insufficientNote: string | null;
  engineLabel: string;
  /** X-axis title naming the span, e.g. `Time, 08 Sep 2026`. */
  xTitle: string;
  n: number;
  xticks: number;
}

const STATE_LABEL: Readonly<Record<SpcResult['state'], string>> = {
  'out-of-control': 'Out of control',
  'in-control': 'In control',
  insufficient: 'Insufficient data',
};
const STATE_TONE: Readonly<Record<SpcResult['state'], SpcStateTone>> = {
  'out-of-control': 'crit',
  'in-control': 'good',
  insufficient: 'muted',
};

/**
 * The machine's I-MR chart: the live replay when it has one (240 samples),
 * else the 11-day tracked metric. `null` when the machine has neither.
 *
 * - Insufficient (< 20 points) → no control limits or signals are drawn; the
 *   data, the mean and the spec limit are, with a note saying why.
 * - Markers group signals by point: `R1`, `R1·R4`.
 */
export function spcView(machine: string, ctx: ReliabilityCtx): SpcView | null {
  const live = liveSeries(machine, ctx.live);
  const series = live ?? dailySeries(machine, ctx.dailyMetric);
  if (!series || !series.points.length) return null;
  const basis = live ? 'live' : 'daily';
  const r = spcEngine.imr(series);
  const insufficient = r.state === 'insufficient';
  const name = LIVE_METRIC_NAME[r.metric] ?? metricInfoFor(r.metric).name;
  const u = r.unit;

  const values = r.points.map((p) => p.value);
  const labels = r.points.map((p) => (basis === 'live' ? (p.x.split('T')[1] ?? p.x).slice(0, 5) : stampLabel(p.x)));

  const hlines: LineHLine[] = insufficient
    ? [{ value: r.mean, label: `mean ${fix(r.mean)}`, tone: 'info', dashed: false, align: 'start' }]
    : [
        { value: r.ucl, label: `UCL ${fix(r.ucl)}`, tone: 'warn', align: 'end' },
        { value: r.mean, label: `CL ${fix(r.mean)}`, tone: 'info', dashed: false, align: 'end' },
        { value: r.lcl, label: `LCL ${fix(r.lcl)}`, tone: 'warn', align: 'end' },
      ];
  if (r.usl !== undefined) hlines.push({ value: r.usl, label: `USL ${r.usl} ${u}`, tone: 'crit', align: 'start' });
  if (r.lsl !== undefined) hlines.push({ value: r.lsl, label: `LSL ${r.lsl} ${u}`, tone: 'crit', align: 'start' });

  const byIndex = new Map<number, { rules: number[]; text: string[] }>();
  for (const s of r.signals) {
    const e = byIndex.get(s.index) ?? { rules: [], text: [] };
    if (!e.rules.includes(s.rule)) e.rules.push(s.rule);
    e.text.push(`Rule ${s.rule}: ${s.description}`);
    byIndex.set(s.index, e);
  }
  const markers: LineMarker[] = [...byIndex].map(([index, e]) => ({
    index,
    label: e.rules.map((k) => `R${k}`).join('·'),
    tone: 'crit',
    title: `${stampLabel(r.points[index].x)} · ${r.points[index].value} ${u} — ${e.text.join('; ')}`,
  }));
  const signals: SignalRow[] = [...byIndex].map(([index, e]) => ({
    key: `sig-${index}`,
    index,
    at: stampLabel(r.points[index].x),
    value: `${r.points[index].value} ${u}`,
    rules: e.rules.map((k) => `Rule ${k}`).join(' + '),
    description: e.text.map((t) => t.replace(/^Rule \d: /, '')).join('; '),
  }));

  const limitVals = insufficient ? [r.mean] : [r.ucl, r.lcl];
  const all = [...values, ...limitVals, ...(r.usl !== undefined ? [r.usl] : []), ...(r.lsl !== undefined ? [r.lsl] : [])];
  const lo = Math.min(...all);
  const hi = Math.max(...all);
  const pad = (hi - lo) * 0.06 || 1;

  const stats: { label: string; value: string; hint?: string }[] = [
    { label: 'Mean (CL)', value: `${fix(r.mean)} ${u}`, hint: `baseline: first ${r.baselineN} points` },
    { label: 'σ (MR̄ / 1.128)', value: fix(r.sigma, 1) },
    insufficient
      ? { label: 'UCL / LCL', value: '—', hint: `needs ${MIN_POINTS} points` }
      : { label: 'UCL / LCL', value: `${fix(r.ucl)} / ${fix(r.lcl)}` },
    {
      label: r.usl !== undefined ? `Cpk vs ${r.usl} ${u}` : 'Cpk',
      value: r.cpk !== undefined ? r.cpk.toFixed(2) : '—',
      hint: r.cpk === undefined ? 'no spec limit' : insufficient ? `provisional — ${r.points.length} points` : r.cpk < 1 ? 'below 1.00 — not capable' : r.cpk < 1.33 ? 'below 1.33 target' : 'capable',
    },
    { label: 'Signals', value: insufficient ? '—' : String(r.signals.length), hint: insufficient ? undefined : `${byIndex.size} points` },
  ];

  return {
    basis,
    title: `${name} · I-MR chart`,
    subtitle:
      basis === 'live'
        ? `${machine} live replay · ${r.points.length} samples, 3 minutes apart · ${stampLabel(r.points[0].x)} – ${stampLabel(r.points[r.points.length - 1].x)}`
        : `${machine} daily mean · ${r.points.length} days · ${stampLabel(r.points[0].x)} – ${stampLabel(r.points[r.points.length - 1].x)} 2026`,
    unit: u,
    labels,
    values,
    mr: r.points.map((p) => p.mr),
    hlines,
    mrHlines: insufficient
      ? []
      : [
          { value: r.mrUcl, label: `UCL ${fix(r.mrUcl)}`, tone: 'warn', align: 'end' },
          { value: r.mrBar, label: `MR̄ ${fix(r.mrBar)}`, tone: 'info', dashed: false, align: 'end' },
        ],
    markers: insufficient ? [] : markers,
    domain: { min: Math.floor(lo - pad), max: Math.ceil(hi + pad) },
    state: r.state,
    stateLabel: STATE_LABEL[r.state],
    stateTone: STATE_TONE[r.state],
    stats,
    signals: insufficient ? [] : signals,
    insufficientNote: insufficient
      ? `${r.points.length} points — an I-MR chart needs at least ${MIN_POINTS} to set control limits, so no limits or run-rule signals are drawn. ${basis === 'daily' ? `${machine} reports shift-end daily means only; a live feed (or a longer history) would chart.` : ''}`.trim()
      : null,
    engineLabel: spcEngine.label,
    xTitle: basis === 'live' ? `Time, ${spanDays(r.points[0].x, r.points[r.points.length - 1].x)} 2026` : 'Date (2026)',
    n: r.points.length,
    // Daily: every third day, so the last label never runs off the right edge.
    xticks: basis === 'live' ? 12 : 4,
  };
}

// ---------------------------------------------------------------------------
// TARP
// ---------------------------------------------------------------------------

/** Band-pill class per TARP level (the TARP seed's colours are the band tokens). */
export const TARP_LEVEL_CLASS: Readonly<Record<TarpLevelName, string>> = {
  Normal: 'band-good',
  'Level 1': 'band-fair',
  'Level 2': 'band-poor',
  'Level 3': 'band-major',
};

const BASIS_LABEL: Readonly<Record<TarpBasis, string>> = {
  live: 'live CAN feed',
  daily: 'shift-end daily mean',
  events: 'alarm-event counts',
};

export interface TarpRungView {
  level: TarpLevelName;
  levelClass: string;
  colour: string;
  trigger: string;
  actions: readonly string[];
  owner: string;
  current: boolean;
}

export interface TarpCardView {
  tarpId: string;
  title: string;
  component: string;
  level: TarpLevelName;
  levelClass: string;
  colour: string;
  since: string | null;
  latest: string | null;
  basis: string;
  trigger: string;
  actions: readonly string[];
  owner: string;
  ladder: TarpRungView[];
}

const RANK: Readonly<Record<TarpLevelName, number>> = { Normal: 0, 'Level 1': 1, 'Level 2': 2, 'Level 3': 3 };

/** Every TARP covering the machine, worst level first, each with its full ladder. */
export function tarpView(machine: string, ctx: ReliabilityCtx): TarpCardView[] {
  const defs = new Map<string, Tarp>(tarpsFor(machine).map((t) => [t.id, t]));
  return tarpStatuses(machine, ctx)
    .slice()
    .sort((a, b) => RANK[b.level] - RANK[a.level] || a.tarpId.localeCompare(b.tarpId))
    .map((s: TarpStatus) => {
      const def = defs.get(s.tarpId);
      return {
        tarpId: s.tarpId,
        title: def?.title ?? s.tarpId,
        component: def?.component ?? '',
        level: s.level,
        levelClass: TARP_LEVEL_CLASS[s.level],
        colour: s.colour,
        since: s.since ? stampLabel(s.since) : null,
        latest: s.latest !== null ? `${s.latest} ${def?.unit ?? ''}`.trim() : null,
        basis: BASIS_LABEL[s.basis],
        trigger: s.trigger,
        actions: s.actions,
        owner: s.owner,
        ladder: (def?.levels ?? []).map((l) => ({
          level: l.level,
          levelClass: TARP_LEVEL_CLASS[l.level],
          colour: l.colour,
          trigger: l.trigger,
          actions: l.actions,
          owner: l.owner,
          current: l.level === s.level,
        })),
      };
    });
}

// ---------------------------------------------------------------------------
// FMEA
// ---------------------------------------------------------------------------

export interface FmeaRowView {
  id: string;
  component: string;
  failureMode: string;
  effect: string;
  cause: string;
  s: number;
  o: number;
  d: number;
  rpn: number;
  /** Band-pill class for the RPN. */
  rpnClass: string;
  occurrenceHow: string;
  detectionHow: string;
  currentControls: string;
  recommendedAction: string;
  owner: string;
  /** This machine's register score for the component, when it carries it. */
  machineScore: number | null;
  tarpId: string | null;
}

const rpnClass = (rpn: number): string =>
  rpn >= 200 ? 'band-vpoor' : rpn >= 120 ? 'band-poor' : rpn >= 60 ? 'band-fair' : 'band-good';

/** The machine's class's failure modes, highest RPN first, with the O and D working. */
export function fmeaView(machine: string, ctx: ReliabilityCtx): FmeaRowView[] {
  const m = BY_ID[machine];
  if (!m) return [];
  const reg = HEALTH.filter((h) => h.machine_id === machine);
  return fmeaRows(ctx)
    .filter((r: FmeaResult) => r.class === m.cls)
    .map((r) => {
      const score = reg.find((h) => h.component === r.component)?.health_score ?? null;
      const credits = [r.tarpId ? `− 2 TARP (${r.tarpId})` : null, r.spcCovered ? '− 1 SPC-able metric' : null].filter(Boolean);
      return {
        id: r.id,
        component: r.component,
        failureMode: r.failureMode,
        effect: r.effect,
        cause: r.cause,
        s: r.severity,
        o: r.occurrence,
        d: r.detection,
        rpn: r.rpn,
        rpnClass: rpnClass(r.rpn),
        occurrenceHow:
          r.occurrenceSource === 'work-orders'
            ? `${r.failures} breakdown/corrective WOs ÷ ${r.machines} machines ÷ ${r.years} y = ${r.ratePerMachineYear} per machine-year → O ${r.occurrence}`
            : `No matching work orders — seeded O ${r.occurrence}`,
        detectionHow: credits.length ? `Base ${r.baseDetection} ${credits.join(' ')} → D ${r.detection}` : `Base ${r.baseDetection}, no monitoring credit → D ${r.detection}`,
        currentControls: r.currentControls,
        recommendedAction: r.recommendedAction,
        owner: r.owner,
        machineScore: score,
        tarpId: r.tarpId,
      };
    });
}

// ---------------------------------------------------------------------------
// Control plan
// ---------------------------------------------------------------------------

export interface ControlPlanRowView extends ControlPlanRow {
  key: string;
  /** The machine's current level on the linked TARP. */
  tarpLevel: TarpLevelName | null;
  tarpLevelClass: string | null;
}

/** The machine's class's control-plan rows, with the linked TARP's current level. */
export function controlPlanView(machine: string, ctx: ReliabilityCtx): ControlPlanRowView[] {
  const m = BY_ID[machine];
  if (!m) return [];
  const levels = new Map(tarpStatuses(machine, ctx).map((s) => [s.tarpId, s.level]));
  return CONTROL_PLANS.filter((c) => c.class === m.cls).map((c, i) => {
    const level = c.tarpId ? levels.get(c.tarpId) ?? null : null;
    return {
      ...c,
      key: `${c.component}-${i}`,
      tarpLevel: level,
      tarpLevelClass: level ? TARP_LEVEL_CLASS[level] : null,
    };
  });
}
